import { ECountry, EOnboardingKey } from '@constants';
import type { IUser } from '@sdk/contracts';
import type { IUserInfo } from 'libs/sdk/src/lib/user-info';
import { useEffect } from 'react';
import { create as createStore } from 'zustand';
import { persist } from 'zustand/middleware';

import { useFeatureFlag } from './useFeatureFlag';

import { useProfile, useUserInfoByUser } from '@/store';

interface IStore {
  isOpen: boolean;
  hasUserDismissedModal: boolean;
  openModal: () => void;
  closeModal: () => void;
  dismissModal: () => void;
  resetDismissal: () => void;
}

const store = createStore<IStore>()(
  persist(
    set => ({
      isOpen: false,
      hasUserDismissedModal: false,
      openModal: () => set({ isOpen: true }),
      closeModal: () => set({ isOpen: false }),
      dismissModal: () => set({ isOpen: false, hasUserDismissedModal: true }),
      resetDismissal: () => set({ hasUserDismissedModal: false, isOpen: true })
    }),
    {
      name: 'fp-old-users-matriculation-modal',
      partialize: state => ({ hasUserDismissedModal: state.hasUserDismissedModal })
    }
  )
);

export const useFPOldUsersMatriculationModalStore = () => store();

const checkMissingFields = (
  profile: IUser | undefined,
  userInfo: IUserInfo | undefined,
  slug: string | undefined
) => {
  if (!profile || !userInfo) {
    return false;
  }

  const isUserFromFP = slug === EOnboardingKey.FP;

  if (!isUserFromFP) {
    return false;
  }

  const profileRequiredFields = [
    profile.name,
    profile.lastName,
    profile.phone,
    profile.legalIdType,
    profile.legalId,
    profile.birthday
  ];

  const userInfoBaseRequiredFields = [
    userInfo.nationality,
    userInfo.gender,
    userInfo.countryBirth,
    userInfo.province,
    userInfo.city,
    userInfo.residenceLocality,
    userInfo.streetType
  ];

  const baseRequiredFields = [...profileRequiredFields, ...userInfoBaseRequiredFields];

  const hasBaseMissingFields = baseRequiredFields.some(field => {
    if (field === undefined) return true;
    if (typeof field === 'string' && field.trim() === '') return true;
    return false;
  });

  const isSecondLastNameMissing =
    profile.secondLastName === undefined ||
    (typeof profile.secondLastName === 'string' && profile.secondLastName.trim() === '');

  let hasBirthLocationMissing = false;

  if (userInfo.countryBirth) {
    const isSpainBirth = userInfo.countryBirth === ECountry.ES;

    if (isSpainBirth) {
      const birthFieldsSpain = [
        userInfo.birthProvince,
        userInfo.birthMunicipality,
        userInfo.birthLocality
      ];
      hasBirthLocationMissing = birthFieldsSpain.some(field => {
        if (field === undefined) return true;
        if (typeof field === 'string' && field.trim() === '') return true;
        return false;
      });
    } else {
      const foreignLocality = userInfo.birthLocalityForeign;
      hasBirthLocationMissing =
        foreignLocality === undefined ||
        (typeof foreignLocality === 'string' && foreignLocality.trim() === '');
    }
  }

  return hasBaseMissingFields || isSecondLastNameMissing || hasBirthLocationMissing;
};

export const useFPOldUsersMatriculationModal = () => {
  const { profile } = useProfile();
  const { userInfo } = useUserInfoByUser(profile?._id || '');
  const { openModal, hasUserDismissedModal } = useFPOldUsersMatriculationModalStore();
  const isForceFPOldUsersMatriculationModal = useFeatureFlag(
    'FORCE_FP_OLD_USERS_MATRICULATION_MODAL'
  );
  const hasMissingFields = checkMissingFields(profile, userInfo, profile?.academy?.slug);

  useEffect(() => {
    const shouldOpenModal = isForceFPOldUsersMatriculationModal
      ? hasMissingFields
      : hasMissingFields && !hasUserDismissedModal;

    if (shouldOpenModal) openModal();
  }, [
    hasMissingFields,
    hasUserDismissedModal,
    openModal,
    profile,
    userInfo,
    isForceFPOldUsersMatriculationModal
  ]);

  return { hasMissingFields, isForceFPOldUsersMatriculationModal };
};
